/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Ajv2020, { type AnySchema, type ErrorObject, type ValidateFunction } from 'ajv/dist/2020';
import addFormats from 'ajv-formats';

export interface JsonConfigurationValidationResult<T> {
    data: T;
    errors: ErrorObject[];
    isValid: boolean;
}

function createValidator<T>(schema: AnySchema, repair: boolean): ValidateFunction<T> {
    const ajv = new Ajv2020({
        allErrors: true,
        removeAdditional: repair ? 'failing' : false,
        useDefaults: repair ? 'empty' : false,
    });

    addFormats(ajv);

    return ajv.compile<T>(schema);
}

/**
 * Validates JSON configuration data and normalizes recoverable schema deviations.
 *
 * Repairing fills schema defaults and removes unknown properties. Invalid values for
 * known properties remain untouched and are reported through the validation result.
 */
export class JsonConfiguration<T> {
    private readonly repairValidator: ValidateFunction<T>;

    private readonly validator: ValidateFunction<T>;

    public constructor(schema: AnySchema) {
        this.validator = createValidator<T>(schema, false);
        this.repairValidator = createValidator<T>(schema, true);
    }

    public validate(data: unknown): JsonConfigurationValidationResult<T> {
        const isValid = this.validator(data);

        return {
            data: data as T,
            errors: [...(this.validator.errors ?? [])],
            isValid,
        };
    }

    public repair(data: unknown): JsonConfigurationValidationResult<T> {
        const repairedData = structuredClone(data);
        const isValid = this.repairValidator(repairedData);

        return {
            data: structuredClone(repairedData) as T,
            errors: [...(this.repairValidator.errors ?? [])],
            isValid,
        };
    }
}
